import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import isEqual from 'lodash-es/isEqual';
import { distinctUntilChanged, map, share, tap } from 'rxjs/operators';

import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { PackagesResultFiltersFacade } from '@packages/packages-result/store/package-result-filters.facade';
import { 
  CheckboxFilter,
  CheckboxFilterVm,
  FilterSorter,
  FilterSorterCriterias,
  PriceRangeFilter
} from '@shared/filters/entities/fetch-filters.response';
import {
  HotelFilterApplier, HotelFilterApplierValue, HotelFilterInputTypes, HotelFilterProvider, HotelFilterTypes
} from '@shared/hotel-itinerary/hotel-filters';
import { HOTEL_FILTER_BAR_TAGS } from '@shared/magic-filter-bar/hotel-filter-bar/mappers/hotel-filters-in-bar.mapper';
import { PackagesResultFacade } from '@packages/packages-result/store/packages-result.facade';
import {
  pickedPackageHotelCheckboxes, pickedPackageHotelSorter, pickedPackageHotelStars
} from '@packages/packages-result/mappers/hotel-package-filters.mapper';
import { removeSubscriptions } from '@shared/helpers/shared-helpers';
import { isEmpty } from '@app/util/util';
import { HotelResultsFacade } from '@hotels/hotel-results/hotel-results.facade';
import { pickedCheckboxes as pickedCheckboxes, pickedStars } from '@hotels/hotel-results/mappers/hotel-filters.mapper';
import { FilterMagicStyle } from '@shared/magic-filter-bar/entities/filter-magic-style';
import {
  MobileFilterMagicContComponent
} from '@shared/magic-filter-bar/mobile-filter-magic-cont/mobile-filter-magic-cont.component';
import { HotelFilterBarService } from '@shared/magic-filter-bar/hotel-filter-bar/services/hotel-filter-bar.service';
import { HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { DEFAULT_HOTEL_SORTER } from '@app/mappers/global-filters/hotel-global-filters.mapper';

@Component({
  selector: 'app-individual-hotel-filter',
  templateUrl: './individual-hotel-filter.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class IndividualHotelFilterContainer implements OnInit, OnDestroy {

  public filter: HotelFilterVm;
  public isHotelByPackage: boolean;
  public selectedStarItems: CheckboxFilter[];
  public resetValue: HotelFilterApplierValue;
  public viewType: HotelFilterProvider;

  public isClearSelectedStars: boolean;

  public pickedCheckboxes = [];
  public pickedStars = [];
  public pickedRadioTotalDiscountPercentage = 0;

  protected hasChangeSortByFilter = false;

  private readonly subscriptions: Subscription[] = [];

  @ViewChild(MobileFilterMagicContComponent) mobileFilterMagicContComponent: MobileFilterMagicContComponent;

  constructor(
    private readonly router: Router,
    private readonly packagesResultFiltersFacade: PackagesResultFiltersFacade,
    private readonly hotelResultsFacade: HotelResultsFacade,
    private readonly packagesResultsFacade: PackagesResultFacade,
    private readonly domManipulateService: DomManipulateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly hotelFilterBarService: HotelFilterBarService,
  ) {
    const currentNav = this.router.getCurrentNavigation();
    if (!currentNav || !currentNav?.extras) {
      return;
    }
    const routerState = currentNav?.extras?.state;
    this.filter = !isEmpty(routerState?.filter) ? routerState?.filter : null;
    this.isHotelByPackage = !isEmpty(routerState?.isHotelByPackage) ? routerState?.isHotelByPackage : false;
    this.resetValue = routerState?.resetValue;
    this.viewType = !isEmpty(routerState?.viewType) ? routerState?.viewType : false;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.pickedCheckboxes$.pipe(
        distinctUntilChanged(),
        tap(checkboxes => {
          if (!isEqual(checkboxes, this.pickedCheckboxes)) {
            this.pickedCheckboxes = checkboxes;
            this.cdr.detectChanges();
          }
        })).subscribe(),
      this.pickedStars$.pipe(
        distinctUntilChanged(),
        tap(stars => {
          if (!isEqual(stars, this.pickedStars)) {
            this.pickedStars = stars;
            this.cdr.detectChanges();
          }
        })).subscribe(),
      this.pickedTotalDiscountPercentage$.pipe(
        distinctUntilChanged(),
        tap(pickedPackageHotelTotalDiscountPercentage => {
          if (!isEqual(pickedPackageHotelTotalDiscountPercentage, this.pickedRadioTotalDiscountPercentage)) {
            this.pickedRadioTotalDiscountPercentage = pickedPackageHotelTotalDiscountPercentage;
            this.cdr.detectChanges();
          }
        })).subscribe(),
      this.sortingHasBeenChanged$.pipe(
        distinctUntilChanged(),
        tap((sortingHasBeenChanged) => {
          this.hasChangeSortByFilter = sortingHasBeenChanged;
          this.cdr.detectChanges();
        })
      ).subscribe()
    );
    this.domManipulateService.setBodyScroll(false);
  }

  ngOnDestroy(): void {
    if (this.viewType === HotelFilterProvider.HotelResults) {
      this.domManipulateService.setBodyScroll(true);
    }
    removeSubscriptions(this.subscriptions);
  }

  public get filterMagicContStyle(): FilterMagicStyle {
    return this.isRadioFilter(this.filter) ? FilterMagicStyle.StyleOne : FilterMagicStyle.StyleTwo;
  }

  public get pickedCheckboxes$(): Observable<string[]> {
    return this.isHotelByPackage ?
      this.packagesResultsFacade.hotelsPayload$.pipe(map(payload => pickedPackageHotelCheckboxes(payload))) :
      this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => pickedCheckboxes(payload)));
  }

  private get pickedTotalDiscountPercentage$(): Observable<number> {
    return this.isHotelByPackage ?
      this.packagesResultsFacade.hotelsPayload$.pipe(map(payload => payload.TotalDiscountPercentageFrom)) :
      this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => payload.TotalDiscountPercentageFrom));
  }

  private get pickedStars$(): Observable<string[]> {
    return this.isHotelByPackage ?
      this.packagesResultsFacade.hotelsPayload$.pipe(map(payload => pickedPackageHotelStars(payload))) :
      this.hotelResultsFacade.hotelFiltersPayload$.pipe(map(payload => pickedStars(payload)));
  }

  private get sortingHasBeenChanged$(): Observable<boolean> {
    return this.hotelFilterBarService.sortingHasBeenChangedByHotel$;
  }

  get pickedPackageHotelSorter$(): Observable<FilterSorterCriterias> {
    return this.packagesResultsFacade.hotelsPayload$.pipe(map(payload => pickedPackageHotelSorter(payload)), share());
  }

  get title(): string {
    return HOTEL_FILTER_BAR_TAGS[this.filter?.type];
  }

  get hasActiveFilter(): boolean {
    switch (true) {
      case this.filter.type === HotelFilterTypes.Sorters:
        return this.hasChangeSortByFilter;
      case this.filter?.filter === HotelFilterInputTypes.Stars:
        return (this.filter?.value as CheckboxFilter[])?.length > 0 &&
          !!(this.filter?.value as CheckboxFilterVm[])?.find(value => this.pickedStars?.includes(value.Text));
      case this.filter?.filter === HotelFilterInputTypes.Checkbox:
        return (this.filter?.value as CheckboxFilter[])?.length > 0 &&
          !!(this.filter?.value as CheckboxFilterVm[])?.find(value => this.pickedCheckboxes?.includes(value.Text));
      case this.filter?.filter === HotelFilterInputTypes.Radio:
        return (this.filter?.value as PriceRangeFilter[])?.length > 0 &&
          !!(this.filter?.value as PriceRangeFilter[])?.find(value =>
            value.LowerBound === this.pickedRadioTotalDiscountPercentage);
      default:
        return false;
    }
  }

  public isStarsFilter(filter: HotelFilterVm): boolean {
    return filter?.filter === HotelFilterInputTypes.Stars && (filter?.value as CheckboxFilter[]).length > 0;
  }

  public isRadioFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Radio && (item.value as PriceRangeFilter[]).length > 0;
  }

  public isSorterFilter(filter: HotelFilterVm): boolean {
    return filter?.type === HotelFilterTypes.Sorters;
  }

  public isCheckboxFilter(filter: HotelFilterVm): boolean {
    return filter?.filter === HotelFilterInputTypes.Checkbox && (filter?.value as CheckboxFilter[]).length > 0;
  }
  
  public clearSelectedStars(value: boolean): void {
    this.isClearSelectedStars = value;
  }

  public applyFilterStar(type: HotelFilterTypes, value: HotelFilterApplierValue): void {
    const event = { type, value };
    this.applyFilterVm(event);
  }

  public applyFilterVm(event: HotelFilterApplier): void {
    if (this.isHotelByPackage) {
      this.applyHotelFilterVmByPackage(event);
    } else {
      this.applyHotelFilterVm(event);
    }

    if (this.isRadioFilter(this.filter)) {
      this.mobileFilterMagicContComponent.close();
    }
  }

  public applySorter(sorter: FilterSorter): void {
    if (this.isHotelByPackage) {
      this.packagesResultFiltersFacade.fetchHotelsWithSorter(sorter);
    } else {
      this.hotelResultsFacade.setFilterSorter(sorter);
    }
    this.hotelFilterBarService.sortingHasBeenChangedByHotel(true);
    this.mobileFilterMagicContComponent.close();
  }

  public clearSelectedFilterValues(): void {
    const { type } = this.filter;
    const payload = { type, value: this.resetValue, removable: true, clear: true };
    this.clearActionByType(this.filter?.type);
    if (this.isHotelByPackage) {
      type === HotelFilterTypes.Sorters ? 
        this.packagesResultFiltersFacade.fetchHotelsWithSorter(DEFAULT_HOTEL_SORTER) :
        this.applyHotelFilterVmByPackage(payload);
    } else {
      this.applyHotelFilterVm(payload);
    }
  }

  private clearActionByType(type: HotelFilterTypes): void {
    if (type === HotelFilterTypes.Category) {
      this.clearSelectedStars(true);
      this.pickedStars = [];
    } else if (type === HotelFilterTypes.Sorters) {
      this.hasChangeSortByFilter = false;
      this.hotelFilterBarService.sortingHasBeenChangedByHotel(false);
      this.cdr.detectChanges();
    } else {
      this.pickedCheckboxes = [];
      this.pickedRadioTotalDiscountPercentage = 0;
    }
  }

  private applyHotelFilterVmByPackage(_filter: HotelFilterApplier): void {
    if (this.viewType === HotelFilterProvider.HotelResults) {
      this.domManipulateService.resetScrollPosition();
      this.packagesResultFiltersFacade.fetchHotelsWithFilters(_filter, true);
    } else {
      this.packagesResultFiltersFacade.fetchHotelsMarkersWithFilters(_filter);
    }
  }

  private applyHotelFilterVm(_filter: HotelFilterApplier): void {
    if (this.viewType === HotelFilterProvider.HotelResults) {
      this.domManipulateService.resetScrollPosition();
      this.hotelResultsFacade.fetchHotelsWithFilters(_filter);
    } else {
      this.hotelResultsFacade.fetchHotelsMarkersWithFilters(_filter);
    }
  }
}
